// Block Quote's color sweep, driven by scroll position instead of played on entry (2026-10-12):
// the on-bg color fills the quote in step with how far it has been scrolled through, and
// scrolling back up un-fills it. Writes --block-quote-progress (0-1) on .block-quote__sweep;
// block-quote.css turns that into the top layer's width.
//
// Progress runs from the quote's top edge reaching START_AT of the viewport height (0) to its
// bottom edge reaching END_AT (1): it starts as soon as the quote peeks in at the bottom, and
// a long one fills over its own height plus that. UNVERIFIED: START_AT/END_AT are our own pick.
//
// Only listens to scroll while a quote is near the viewport (IntersectionObserver), and does at
// most one pass per frame: all reads first, then only the writes that change something. The
// server renders the quotes in full color (so they're shown if this never runs); the observer's
// first report mutes the ones still below the screen. Without IntersectionObserver, or with
// reduced motion, the quote is just shown in full.

const START_AT = 1;
const END_AT = 0.75;

export function initBlockQuote(): void {
    const sweeps = [...document.querySelectorAll<HTMLElement>(".block-quote__sweep")];
    if (sweeps.length === 0) {
        return;
    }

    if (!("IntersectionObserver" in window) || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        sweeps.forEach((sweep) => sweep.style.setProperty("--block-quote-progress", "1"));
        return;
    }

    const active = new Set<HTMLElement>();
    const written = new WeakMap<HTMLElement, string>();
    let frame = 0;

    const setProgress = (sweep: HTMLElement, value: string): void => {
        if (written.get(sweep) !== value) {
            written.set(sweep, value);
            sweep.style.setProperty("--block-quote-progress", value);
        }
    };

    const update = (): void => {
        frame = 0;
        const viewportHeight = window.innerHeight;
        const values = [...active].map((sweep) => {
            const rect = sweep.getBoundingClientRect();
            const distance = (START_AT - END_AT) * viewportHeight + rect.height;
            const progress = Math.min(Math.max((START_AT * viewportHeight - rect.top) / distance, 0), 1);
            return [sweep, progress === 0 || progress === 1 ? String(progress) : progress.toFixed(4)] as const;
        });
        values.forEach(([sweep, value]) => setProgress(sweep, value));
    };

    const schedule = (): void => {
        if (!frame) {
            frame = requestAnimationFrame(update);
        }
    };

    // Observed with a margin so a quote is already tracked a little before it scrolls into view,
    // and its last update (fully filled or fully muted) lands before it's dropped.
    const observer = new IntersectionObserver(
        (entries) => {
            for (const entry of entries) {
                const sweep = entry.target as HTMLElement;
                if (entry.isIntersecting) {
                    active.add(sweep);
                } else {
                    active.delete(sweep);
                    // Off screen: settle it on the side it left from, so a fast scroll past it
                    // never leaves it half filled.
                    setProgress(sweep, entry.boundingClientRect.top < 0 ? "1" : "0");
                }
            }
            if (active.size > 0) {
                window.addEventListener("scroll", schedule, { passive: true });
                window.addEventListener("resize", schedule, { passive: true });
                schedule();
            } else {
                window.removeEventListener("scroll", schedule);
                window.removeEventListener("resize", schedule);
            }
        },
        { rootMargin: "25% 0px" },
    );
    sweeps.forEach((sweep) => observer.observe(sweep));
}
